import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import * as XLSX from "xlsx";
import { InboxOutlined } from "@ant-design/icons";
import { message, Radio, Spin, Upload } from "antd";
import { CiEdit } from "react-icons/ci";
import { BiSolidTrash } from "react-icons/bi";

import "./styles.scss";
import CustomModal from "../../../assets/component/modal";
import Breadcrumbs from "../../../assets/component/breadcrumbs";
import CustomPlaybookSection from "../custom-playbook/playbook-tabs";
import { ConfirmationModal } from "../../custom-playbook/manage-playbook";
import { CreateCustomPlaybookAction, createCustomPlaybookReset } from "../../../store/slices/custom-playbook/createPlaybook";
import { CustomPlaybookExportAction, customPlaybookExportReset } from "../../../store/slices/custom-playbook/export-playbook";
import { RetrieveCustomPlaybookListAction, retrieveCustomPlaybookListReset } from "../../../store/slices/custom-playbook/retrievePlaybookList";
import { UpdateCustomPlaybookQuestionsAction, updateCustomPlaybookQuestionsReset } from "../../../store/slices/custom-playbook/updatePlaybookQuestions";
import { ImportCustomPlaybookQuestionsAction, importCustomPlaybookQuestionsReset } from "../../../store/slices/custom-playbook/importPlaybookQuestions";

const { Dragger } = Upload;

const visibilityOptions = [
    { label: "Org", value: "org" },
    { label: "User", value: "user" },
];

const CUSTOM_PLAYBOOK_PREFIX = "playbook-";

export const stripPlaybookPrefix = (frameworkName) => frameworkName?.startsWith(CUSTOM_PLAYBOOK_PREFIX)
    ? frameworkName.slice(CUSTOM_PLAYBOOK_PREFIX.length)
    : frameworkName;

const CustomPlaybookTabs = ({ tabOptions, setTabValue, tabValue, children }) => {

    const handleSelectTab = (tab) => {
        setTabValue(tab)
    }

    return (
        <div className="tabs-main">
            <div className="tab-header">
                {tabOptions?.map(tab => {
                    return (
                        <div className={`tab-header-contents ${tabValue?.label === tab?.label ? 'selected-tab' : 'unselected-tab'}`}
                            onClick={() => handleSelectTab(tab)}>{tab?.label}
                        </div>
                    )
                })}
            </div>
            <div className="tab-body-container">{children}</div>
        </div>
    )
}

export const CreatePlaybookForm = ({ handleClose, onCreated, isEditModal, currentPlaybookName, currentVisibility, onUpdated, onDeleted }) => {
    const dispatch = useDispatch();

    const [playbookName, setPlaybookName] = useState(currentPlaybookName || "");
    const [visibility, setVisibility] = useState(currentVisibility || null);
    const [formErrors, setFormErrors] = useState({});
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [pendingEditAction, setPendingEditAction] = useState(null);

    const { isLoading: createPlaybookLoading, success: createPlaybookSuccess, error: createPlaybookError, data: createPlaybookData } = useSelector(state => state?.createCustomPlaybook) || {};
    const { isLoading: updatePlaybookLoading, success: updatePlaybookSuccess, error: updatePlaybookError, data: updatePlaybookData } = useSelector(state => state?.updateCustomPlaybookQuestions) || {};

    const onFieldChange = (field, value) => {
        if (field === "playbookName") setPlaybookName(value);
        if (field === "visibility") setVisibility(value);
        setFormErrors(prev => ({ ...prev, [field]: "" }));
    };

    const handleSubmit = () => {
        setIsSubmitted(true);

        const errors = {};
        if (!playbookName?.trim()) errors.playbookName = "Please enter the playbook name";
        if (!visibility) errors.visibility = "Please select the visibility";

        setFormErrors(errors);
        if (Object.keys(errors).length > 0) return;

        const trimmedName = playbookName?.trim();

        if (isEditModal) {
            const payload = { isPlaybook: true, currentPlaybookName, visibility };
            if (trimmedName !== currentPlaybookName) payload.playbookName = trimmedName;
            setPendingEditAction("update");
            dispatch(UpdateCustomPlaybookQuestionsAction(payload));
            return;
        }

        dispatch(CreateCustomPlaybookAction({ playbookName: trimmedName, visibility }));
    };

    const handleDeletePlaybook = () => {
        setPendingEditAction("delete");
        dispatch(UpdateCustomPlaybookQuestionsAction({ isPlaybook: true, currentPlaybookName, visibility: currentVisibility || visibility, isDelete: true }));
    };

    useEffect(() => {
        if (createPlaybookSuccess) {
            message.success(createPlaybookData?.message || "Playbook created successfully");
            dispatch(createCustomPlaybookReset());
            onCreated?.();
        }
        if (createPlaybookError) {
            message.error(createPlaybookError?.message || "Failed to create playbook");
            dispatch(createCustomPlaybookReset());
        }
    }, [createPlaybookSuccess, createPlaybookError])

    useEffect(() => {
        if (!isEditModal) return;
        if (updatePlaybookSuccess) {
            message.success(updatePlaybookData?.message || "Playbook updated successfully");
            dispatch(updateCustomPlaybookQuestionsReset());
            if (pendingEditAction === "delete") {
                onDeleted?.();
            } else {
                onUpdated?.(playbookName?.trim());
            }
            setPendingEditAction(null);
        }
        if (updatePlaybookError) {
            message.error(updatePlaybookError?.message || "Failed to update playbook");
            dispatch(updateCustomPlaybookQuestionsReset());
        }
    }, [updatePlaybookSuccess, updatePlaybookError])

    const isLoading = isEditModal ? updatePlaybookLoading : createPlaybookLoading;

    return (
        <div className="create-playbook-form">
            <div className="input-wrapper">
                <div className="input-label">Playbook Name<span>*</span></div>
                <div className="text-wrapper">
                    <input
                        type="text"
                        className="text-input"
                        placeholder="Enter playbook name"
                        value={playbookName}
                        onChange={(e) => onFieldChange("playbookName", e.target.value)}
                    />
                </div>
                {isSubmitted && formErrors.playbookName && <div className="error-message">{formErrors.playbookName}</div>}
            </div>
            <div className="input-wrapper">
                <div className="input-label">Visibility<span>*</span></div>
                <Radio.Group
                    className="visibility-radio"
                    options={visibilityOptions}
                    value={visibility}
                    onChange={(e) => onFieldChange("visibility", e?.target?.value)}
                />
                {isSubmitted && formErrors.visibility && <div className="error-message">{formErrors.visibility}</div>}
            </div>
            <div className="action-buttons">
                {isLoading ? (
                    <div className="button-wrapper submit"><Spin size="small" /></div>
                ) : (
                    <div className="button-wrapper submit" onClick={handleSubmit}>{isEditModal ? "Update" : "Create"}</div>
                )}
                <div className="button-wrapper cancel" onClick={handleClose}>Cancel</div>
            </div>
        </div>
    )
}

const CreatePlaybook = () => {
    return (
        <div className="create-playbook-main">
            <div className="no-playbook-text">No playbook found</div>
        </div>
    )
}

const CustomPlaybook = () => {
    const [activeTab, setActiveTab] = useState("");
    const [showCreatePlaybookModal, setShowCreatePlaybookModal] = useState(false);
    const [showRowEditModal, setShowRowEditModal] = useState(false);
    const [editFormValues, setEditFormValues] = useState({ theme: "", question: "" });
    const [showImportModal, setShowImportModal] = useState(false);
    const [showPlaybookDeleteModal, setShowPlaybookDeleteModal] = useState(false);
    const [fileList, setFileList] = useState([]);
    const [file, setFile] = useState(null);
    const [isOwner, setIsOwner] = useState(false);
    const [visibility, setVisibility] = useState(null);
    const [selectedQuestion, setSelectedQuestion] = useState(null);
    const [formErrors, setFormErrors] = useState({});
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [showPlaybookEditModal, setShowPlaybookEditModal] = useState(false);
    const [pendingPlaybookAction, setPendingPlaybookAction] = useState(null);

    const { data: retrievedPlaybookData, isLoading: playbookLoading } = useSelector(state => state?.retrieveCustomPlaybookList) || {};
    const { success: importSuccess, error: importError, isLoading: importLoading } = useSelector(state => state?.importCustomPlaybookQuestions) || {};
    const { isLoading: exportLoading, success: exportSuccess, error: exportError } = useSelector(state => state?.customPlaybookExport) || {};

    const dispatch = useDispatch();

    const fetchPlaybooks = () => dispatch(RetrieveCustomPlaybookListAction({}));

    useEffect(() => {
        fetchPlaybooks();
        return () => dispatch(retrieveCustomPlaybookListReset());
    }, [])

    const hasPlaybooks = retrievedPlaybookData?.data?.length > 0;

    const playbookDataOptions = useMemo(() => {
        if (retrievedPlaybookData?.data?.length > 0) {
            return retrievedPlaybookData?.data?.map(frameworkName => {
                const playbookName = stripPlaybookPrefix(frameworkName);
                return { label: playbookName, value: playbookName };
            })
        }
    }, [retrievedPlaybookData])

    const handleCreatePlaybookSuccess = () => {
        setShowCreatePlaybookModal(false);
        fetchPlaybooks();
    }

    useEffect(() => {
        if (!activeTab && playbookDataOptions?.length > 0) {
            setActiveTab(playbookDataOptions[0]);
        }
    }, [playbookDataOptions])

    const handlePlaybookRenamed = (newPlaybookName) => {
        fetchPlaybooks();
        setActiveTab({ label: newPlaybookName, value: newPlaybookName });
    }

    const handlePlaybookDeleted = () => {
        fetchPlaybooks();
        setActiveTab("");
    }

    const handleAddClick = () => {
        setSelectedQuestion(null);
        setEditFormValues({ theme: "", question: "" });
        setFormErrors({});
        setIsSubmitted(false);
        setShowRowEditModal(true);
    }

    const handleDeletePlaybook = () => {
        setShowPlaybookDeleteModal(true);
    }

    const handleConfirmDeletePlaybook = () => {
        if (!activeTab?.value) return;
        setPendingPlaybookAction("delete");
        dispatch(UpdateCustomPlaybookQuestionsAction({
            isPlaybook: true,
            currentPlaybookName: activeTab?.value,
            isDelete: true,
            visibility: visibility
        }));
    }

    const handleOwnershipUpdate = (ownerStatus) => {
        setIsOwner(ownerStatus);
    }

    const handleVisibilityUpdate = (visibilityValue) => {
        setVisibility(visibilityValue);
    }

    const handleExportPlaybook = () => {
        if (!activeTab?.value) return;
        dispatch(CustomPlaybookExportAction({ playbookName: activeTab?.value }));
    }

    const validateFile = (uploadFile) => {
        const isValidType = uploadFile?.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
            uploadFile?.type === "application/vnd.ms-excel";

        if (!isValidType) {
            message.error("Only Excel files are allowed");
            return false;
        }
        return true;
    };

    const uploadProps = {
        maxCount: 1,
        accept: ".xlsx,.xls",
        fileList,
        beforeUpload: (uploadFile) => {
            if (!validateFile(uploadFile)) return Upload.LIST_IGNORE;

            setFile(uploadFile);
            setFileList([{ uid: uploadFile?.uid, name: uploadFile?.name, status: "done" }]);
            return false;
        },
        onRemove: () => {
            setFile(null);
            setFileList([]);
        },
    };

    const handleDownloadTemplate = () => {
        const headers = [
            "Theme",
            "Question"
        ];

        const rowData = [
            "<Sample> Risk Management",
            "<Sample> What are the measures taken by the company for climate risk management?"
        ]

        const data = [headers, rowData];
        const worksheet = XLSX.utils.aoa_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
        XLSX.writeFile(workbook, "Questions_Template.xlsx");
    };

    const handleImportQuestions = () => {
        if (!file) {
            message.error("Please select a file to import");
            return;
        }

        const formData = new FormData();
        formData.append("playbookName", activeTab?.value);
        formData.append("file", file);
        dispatch(ImportCustomPlaybookQuestionsAction(formData));
    };

    const handleCloseImportModal = () => {
        setShowImportModal(false);
        setFile(null);
        setFileList([]);
    };

    useEffect(() => {
        if (importSuccess) {
            message.success("Saved successfully");
            dispatch(importCustomPlaybookQuestionsReset());
            handleCloseImportModal();
            fetchPlaybooks();
        }
        if (importError) {
            message.error(importError?.message || "Failed to save");
            dispatch(importCustomPlaybookQuestionsReset());
        }
    }, [importSuccess, importError])

    useEffect(() => {
        if (exportSuccess) {
            message.success("Playbook questions exported successfully");
            dispatch(customPlaybookExportReset());
        }
        if (exportError) {
            message.error(exportError?.message || "Failed to export playbook questions");
            dispatch(customPlaybookExportReset());
        }
    }, [exportSuccess, exportError])

    const { data: updatePlaybookData, success: updatePlaybookSuccess, error: updatePlaybookError } = useSelector(state => state?.updateCustomPlaybookQuestions) || {};

    useEffect(() => {
        if (updatePlaybookSuccess) {
            message.success(updatePlaybookData?.message);
            dispatch(updateCustomPlaybookQuestionsReset());
            setShowPlaybookDeleteModal(false);
            if (pendingPlaybookAction === "delete") {
                handlePlaybookDeleted();
            }
            setPendingPlaybookAction(null);
        }
        if (updatePlaybookError) {
            message.error(updatePlaybookError?.message || "Failed to delete playbook");
            dispatch(updateCustomPlaybookQuestionsReset());
            setPendingPlaybookAction(null);
        }
    }, [updatePlaybookSuccess, updatePlaybookError])

    return (
        <div className="custom-playbook-main">
            <Breadcrumbs />
            <div className="import-export-playbook-container">
                <div className="action-buttons">
                    <div className="button-wrapper create" onClick={() => setShowCreatePlaybookModal(true)}>
                        + Create Playbook
                    </div>
                    {hasPlaybooks && activeTab && isOwner && (
                        <>
                            <div className="button-wrapper add" onClick={handleAddClick}>
                                + Add Question
                            </div>
                            <div className="button-wrapper add" onClick={() => setShowPlaybookEditModal(true)}>
                                <CiEdit /> Edit Playbook
                            </div>
                            <div className="button-wrapper delete" onClick={handleDeletePlaybook}>
                                <BiSolidTrash /> Delete Playbook
                            </div>
                            <div className="button-wrapper import" onClick={() => setShowImportModal(true)}>
                                Import Questions
                            </div>
                        </>
                    )}
                    {hasPlaybooks && <div className="button-wrapper export" onClick={handleExportPlaybook}>
                        {exportLoading ? "Exporting..." : "Export Questions"}
                    </div>}
                </div>
            </div>
            {playbookLoading ? <Spin size="large" /> :
                hasPlaybooks ?
                    <CustomPlaybookTabs tabOptions={playbookDataOptions} setTabValue={setActiveTab} tabValue={activeTab}>
                        {activeTab && (
                            <div className="playbook-content">
                                <CustomPlaybookSection
                                    playbookId={activeTab}
                                    onPlaybookRenamed={handlePlaybookRenamed}
                                    onPlaybookDeleted={handlePlaybookDeleted}
                                    showRowEditModal={showRowEditModal}
                                    setShowRowEditModal={setShowRowEditModal}
                                    onOwnershipUpdate={handleOwnershipUpdate}
                                    onVisibilityUpdate={handleVisibilityUpdate}
                                    editFormValues={editFormValues}
                                    setEditFormValues={setEditFormValues}
                                    selectedQuestion={selectedQuestion}
                                    setSelectedQuestion={setSelectedQuestion}
                                    formErrors={formErrors}
                                    setFormErrors={setFormErrors}
                                    isSubmitted={isSubmitted}
                                    setIsSubmitted={setIsSubmitted}
                                    showPlaybookEditModal={showPlaybookEditModal}
                                    setShowPlaybookEditModal={setShowPlaybookEditModal}
                                />
                            </div>
                        )}
                    </CustomPlaybookTabs> : (
                        <CreatePlaybook />
                    )}
            {showCreatePlaybookModal && (
                <CustomModal
                    handleClose={() => setShowCreatePlaybookModal(false)}
                    modalTitle="Create Playbook"
                    showCloseIcon
                >
                    <CreatePlaybookForm
                        handleClose={() => setShowCreatePlaybookModal(false)}
                        onCreated={handleCreatePlaybookSuccess}
                    />
                </CustomModal>
            )}
            {showImportModal && (
                <CustomModal
                    handleClose={handleCloseImportModal}
                    modalTitle="Import Questions"
                    showCloseIcon
                >
                    <div className="import-questions-form">
                        <div className="input-wrapper">
                            <Upload.Dragger {...uploadProps} className="upload-dragger">
                                <p className="ant-upload-drag-icon">
                                    <InboxOutlined />
                                </p>
                                <p className="ant-upload-text">Click or drag file to this area to upload</p>
                                <p className="ant-upload-hint">Accepts only Excel file (.xlsx, .xls)</p>
                            </Upload.Dragger>
                        </div>
                        <div style={{ color: "red", fontSize: "12px" }}><sup>*</sup>Please download the template and use the same format for import</div>
                        <div className="button-wrapper template" onClick={handleDownloadTemplate}>Template</div>
                        <div className="action-buttons">
                            <div className={`button-wrapper submit ${importLoading ? 'disabled' : ''}`} onClick={() => !importLoading && handleImportQuestions()}>
                                {importLoading ? "Importing..." : "Import"}
                            </div>
                            <div className="button-wrapper cancel" onClick={handleCloseImportModal}>
                                Cancel
                            </div>
                        </div>
                    </div>
                </CustomModal>
            )}
            <ConfirmationModal
                showModal={showPlaybookDeleteModal}
                handleClose={() => setShowPlaybookDeleteModal(false)}
                message={
                    <>
                        Are you sure you want to delete <strong>{activeTab?.label}</strong> ?
                    </>
                }
                handleConfirm={handleConfirmDeletePlaybook}
            />
        </div>
    )
}

export default CustomPlaybook;